<template>
  <Layout>
    <section class="saved-carts">
      <header>
        <h2>{{ 'Saved Carts' | t }}</h2>
      </header>
      <main>
        <SloLoader key="saved-carts-loader" :loading="savedCartsLoading" size="xl">
          <div v-if="savedCarts.length > 0">
            <transition name="fade-up">
              <SloModal v-if="makeActiveModal" :max-width="500" :form="false" @dismiss="makeActiveModal = false">
                <template v-slot:title>{{ 'Make cart active' | t }}</template>
                <p>{{ 'By making this cart active your current cart will be lost.' | t }}</p>
                <template v-slot:footer>
                  <SloButton @click="handleMakeCartActive" :disabled="makeCartActiveLoading" fill="outline" class="flex-1">
                    <SloLoader :loading="makeCartActiveLoading" size="md">
                      {{ 'Yes, make cart active' | t }}
                    </SloLoader>
                  </SloButton>
                  <SloButton @click="makeActiveModal = false" :disabled="makeCartActiveLoading" class="flex-1">
                    {{ 'No, keep my current cart' | t }}
                  </SloButton>
                </template>
              </SloModal>
              <SloModal v-if="deleteCartModal" :max-width="500" :form="false" @dismiss="deleteCartModal = false">
                <template v-slot:title>{{ 'Delete cart' | t }}</template>
                <p>{{ 'Are you sure you want to delete this saved cart?' | t }}</p>
                <template v-slot:footer>
                  <SloButton @click="handleDeleteCart" :disabled="deleteCartLoading" fill="outline" class="flex-1">
                    <SloLoader :loading="deleteCartLoading" size="md">
                      {{ 'Yes, delete saved cart' | t }}
                    </SloLoader>
                  </SloButton>
                  <SloButton @click="deleteCartModal = false" :disabled="deleteCartLoading" class="flex-1">
                    {{ 'No, keep my saved cart' | t }}
                  </SloButton>
                </template>
              </SloModal>
            </transition>
            <div v-for="savedCart in savedCarts" :key="savedCart.guid">
              <MarketplaceTable>
                <template v-slot:header>
                  <MarketplaceTableHeader>
                    <template v-slot:titles>
                      <div class="flex items-start justify-between md:flex-col md:justify-start">
                        <h4 class="text-base fw-strong">{{ 'Title' | t }}</h4>
                        <span class="text-base c-gray-700">{{ savedCart.name }}</span>
                      </div>
                      <div class="flex items-start justify-between md:flex-col md:justify-start">
                        <h4 class="text-base fw-strong">{{ 'Date Saved' | t }}</h4>
                        <span class="text-base c-gray-700">{{ savedCart.saveTime | formatDate }}</span>
                      </div>
                      <div class="flex items-start justify-between md:flex-col md:justify-start">
                        <h4 class="text-base fw-strong">{{ 'Items' | t }}</h4>
                        <span class="text-base c-gray-700">{{ savedCart.totalUnitCount }}</span>
                      </div>
                      <div class="flex items-start justify-between md:flex-col md:justify-start">
                        <h4 class="text-base fw-strong">{{ 'Total' | t }}</h4>
                        <span class="text-base c-gray-700">{{ savedCart.totalPriceWithTax?.formattedValue }}</span>
                      </div>
                    </template>
                    <template v-slot:buttons>
                      <SloButton @click="openMakeActiveModal(savedCart.code)" fill="outline">
                        {{ 'Make Active' | t }}
                      </SloButton>
                      <SloButton @click="openDeleteCartModal(savedCart.code)" fill="outline">
                        {{ 'Delete Cart' | t }}
                      </SloButton>
                    </template>
                  </MarketplaceTableHeader>
                </template>
                <template v-slot:body>
                  <MarketplaceRow
                    v-for="entry in savedCart?.cartEntries.slice(0, savedCartEntriesDisplayLength[savedCart.code] || 3)"
                    :key="entry?.product?.code"
                    :title="entry?.product?.number"
                    :description="entry?.product?.title"
                    :sku="entry?.product?.code"
                    :product-type="entry?.product?.type"
                    :price="entry?.product?.price?.formattedValue"
                    :uom="entry?.product?.uom"
                    :quantity="entry?.quantity"
                    :total="entry?.totalPrice?.formattedValue"
                  >
                    <template v-slot:image>
                      <SloProductImage :source="entry?.product?.images | getProductThumbnail" />
                    </template>
                  </MarketplaceRow>
                </template>
                <template v-slot:footer v-if="savedCart?.cartEntries.length > 3">
                  <MarketplaceTableFooter>
                    <SloButton
                        v-if="savedCartEntriesDisplayLength[savedCart?.code]"
                        @click="removeSavedCartOrdersEntriesDisplayLengthItem(savedCart)"
                        fill="clear"
                        class="flex items-center gap-xs p-0 bg-transparent c-blue-400 hover:c-blue-700"
                      >
                        <SloIcon name="Minus" />
                        {{ 'Collapse' | t }}
                      </SloButton>
                      <div v-else class="flex items-baseline justify-start gap-sm">
                        <SloButton
                          @click="addSavedCartOrdersEntriesDisplayLengthItem(savedCart)"
                          fill="clear"
                          class="flex items-center gap-xs p-0 bg-transparent c-blue-400 hover:c-blue-700"
                        >
                          <SloIcon name="Plus" />
                          {{ savedCart?.cartEntries.length - 3 }}
                          {{ 'more items' | t }}
                        </SloButton>
                        <div class="marketplace--saved-cart-more-items">
                          <SloProductImage
                            v-for="(orderEntry, moreOrderEntryKey) in savedCart?.cartEntries.slice(3, savedCart?.cartEntries.length)"
                            :key="`more-order-entry-${moreOrderEntryKey}`"
                            :source="orderEntry?.product?.images | getProductThumbnail"
                          />
                        </div>
                      </div>
                  </MarketplaceTableFooter>
                </template>
              </MarketplaceTable>
            </div>
          </div>
          <EmptyCartTeaser v-else>
            <template v-slot:header>
              <h2 class="text-xl fw-strong">{{ 'You have no saved carts' | t }}</h2>
              <p class="m-0">{{ 'Shop the following categories for Sloan parts' | t }}</p>
            </template>
          </EmptyCartTeaser>
        </SloLoader>
      </main>
    </section>
  </Layout>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import t from '../../main/i18n';
import formatDate from '../../../lib/filters/formatDate';
import {getProductThumbnail, handleApiErrors} from '../../main/utils';
import {marketplaceApi} from '../apis/marketplace';
import Layout from '../components/Layout.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import MarketplaceTable from '../components/MarketplaceTable.vue';
import MarketplaceTableHeader from '../components/MarketplaceTableHeader.vue';
import MarketplaceRow from '../components/MarketplaceRow.vue';
import EmptyCartTeaser from '../components/EmptyCartTeaser.vue';
import MarketplaceTableFooter from '../components/MarketplaceTableFooter.vue';

export default {
  components: {
    Layout,
    SloLoader,
    SloProductImage,
    SloButton,
    SloModal,
    SloIcon,
    MarketplaceTable,
    MarketplaceTableHeader,
    MarketplaceRow,
    EmptyCartTeaser,
    MarketplaceTableFooter
  },
  filters: {
    t,
    formatDate,
    getProductThumbnail
  },
  data() {
    return {
      currentCartId: null,
      makeActiveModal: false,
      makeCartActiveLoading: false,
      deleteCartModal: false,
      deleteCartLoading: false,
      savedCartEntriesDisplayLength: []
    };
  },
  async mounted() {
    await this.getSavedCarts();
  },
  computed: {
    ...mapState('marketplace', ['savedCartsLoading', 'savedCarts', 'cartLoading'])
  },
  methods: {
    ...mapActions('marketplace', ['getSavedCarts', 'deleteCart']),
    openMakeActiveModal(cartId) {
      this.makeActiveModal = true;
      this.currentCartId = cartId;
    },
    openDeleteCartModal(cartId) {
      this.deleteCartModal = true;
      this.currentCartId = cartId;
    },
    addSavedCartOrdersEntriesDisplayLengthItem(savedCart) {
      this.savedCartEntriesDisplayLength[savedCart?.code] = savedCart?.cartEntries.length;
      this.$forceUpdate();
    },
    removeSavedCartOrdersEntriesDisplayLengthItem(savedCart) {
      delete this.savedCartEntriesDisplayLength[savedCart?.code];
      this.$forceUpdate();
    },
    async handleMakeCartActive() {
      this.makeCartActiveLoading = true;
      await marketplaceApi.makeCartActive(this.currentCartId)
        .then(async() => {
          this.makeCartActiveLoading = false;
          await this.$router.push({name: 'marketplace-cart'});
          this.$snotify.success(t('Active cart updated'));
        })
        .catch(() => {
          this.makeCartActiveLoading = false;
          this.$snotify.error(t('Unable to make cart active'));
        });
    },
    async handleDeleteCart() {
      this.deleteCartLoading = true;
      await this.deleteCart(this.currentCartId)
        .then(async() => {
          this.deleteCartModal = false;
          this.deleteCartLoading = false;
          await this.getSavedCarts();
          this.$snotify.success(t('Cart deleted'));
        }).catch(e => {
          this.deleteCartLoading = false;
          handleApiErrors(e, this, 'Unable to delete cart');
        });
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.saved-carts {
  :deep(.slo-modal-frame) {
    header button {
      padding: 0 !important;
    }
  }
}

.marketplace--saved-cart-more-items {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.8rem;

  :deep(.slo-product-image) {
    img {
      width: 3.6rem;
      height: 3.6rem;
    }
  }
}

.fade-up-enter, .fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.fade-up-enter-active, .fade-up-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
</style>
